/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  HookSourceUsage,
  HookSourceUsage$inboundSchema,
} from "./hooksourceusage.js";
import { ToolUsage, ToolUsage$inboundSchema } from "./toolusage.js";
import { UserAccount, UserAccount$inboundSchema } from "./useraccount.js";

/**
 * Aggregated usage summary for a single user
 */
export type UserSummary = {
  /**
   * Distinct account types observed for this user ('team', 'personal')
   */
  accountTypes?: Array<string> | undefined;
  /**
   * Linked AI accounts for this user (team and personal, across providers)
   */
  accounts?: Array<UserAccount> | undefined;
  /**
   * Average tokens per chat request
   */
  avgTokensPerRequest: number;
  /**
   * Sum of cache creation input tokens
   */
  cacheCreationInputTokens: number;
  /**
   * Sum of cache read input tokens
   */
  cacheReadInputTokens: number;
  /**
   * Earliest activity timestamp in Unix nanoseconds
   */
  firstSeenUnixNano: string;
  /**
   * Per-hook-source usage breakdown
   */
  hookSources: Array<HookSourceUsage>;
  /**
   * Latest activity timestamp in Unix nanoseconds
   */
  lastSeenUnixNano: string;
  /**
   * Failed tool calls (4xx/5xx status)
   */
  toolCallFailure: number;
  /**
   * Successful tool calls (2xx status)
   */
  toolCallSuccess: number;
  /**
   * Per-tool usage breakdown
   */
  tools: Array<ToolUsage>;
  /**
   * Total number of chat completion requests
   */
  totalChatRequests: number;
  /**
   * Number of unique chat sessions
   */
  totalChats: number;
  /**
   * Total cost of all requests
   */
  totalCost: number;
  /**
   * Sum of input tokens used
   */
  totalInputTokens: number;
  /**
   * Sum of output tokens used
   */
  totalOutputTokens: number;
  /**
   * Sum of all tokens used
   */
  totalTokens: number;
  /**
   * Total number of tool calls
   */
  totalToolCalls: number;
  /**
   * User email associated with this usage, when present
   */
  userEmail: string;
  /**
   * User identifier (user_id or external_user_id depending on group_by)
   */
  userId: string;
};

/** @internal */
export const UserSummary$inboundSchema: z.ZodMiniType<UserSummary, unknown> = z
  .pipe(
    z.object({
      account_types: z.optional(z.array(z.string())),
      accounts: z.optional(z.array(UserAccount$inboundSchema)),
      avg_tokens_per_request: z.number(),
      cache_creation_input_tokens: z.int(),
      cache_read_input_tokens: z.int(),
      first_seen_unix_nano: z.string(),
      hook_sources: z.array(HookSourceUsage$inboundSchema),
      last_seen_unix_nano: z.string(),
      tool_call_failure: z.int(),
      tool_call_success: z.int(),
      tools: z.array(ToolUsage$inboundSchema),
      total_chat_requests: z.int(),
      total_chats: z.int(),
      total_cost: z.number(),
      total_input_tokens: z.int(),
      total_output_tokens: z.int(),
      total_tokens: z.int(),
      total_tool_calls: z.int(),
      user_email: z.string(),
      user_id: z.string(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "account_types": "accountTypes",
        "avg_tokens_per_request": "avgTokensPerRequest",
        "cache_creation_input_tokens": "cacheCreationInputTokens",
        "cache_read_input_tokens": "cacheReadInputTokens",
        "first_seen_unix_nano": "firstSeenUnixNano",
        "hook_sources": "hookSources",
        "last_seen_unix_nano": "lastSeenUnixNano",
        "tool_call_failure": "toolCallFailure",
        "tool_call_success": "toolCallSuccess",
        "total_chat_requests": "totalChatRequests",
        "total_chats": "totalChats",
        "total_cost": "totalCost",
        "total_input_tokens": "totalInputTokens",
        "total_output_tokens": "totalOutputTokens",
        "total_tokens": "totalTokens",
        "total_tool_calls": "totalToolCalls",
        "user_email": "userEmail",
        "user_id": "userId",
      });
    }),
  );

export function userSummaryFromJSON(
  jsonString: string,
): SafeParseResult<UserSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserSummary' from JSON`,
  );
}
